/**
 * 工厂端 UI 标准 —— **七个页面唯一允许引的样式出处**。
 *
 * ── 这份文件解决的问题 ────────────────────────────────────────
 *
 * 改之前七个页面各写各的:同一个「卡片」有 `rounded-xl border-slate-200`、
 * `rounded-lg border-slate-300`、`rounded-2xl shadow-sm` 三种写法,同一个
 * 「次要文字」有 `text-slate-400` / `text-slate-500` / `text-slate-600`。
 * 单看每一页都不难看,连起来就是「拼出来的」—— 而工厂端是拿去卖的东西,
 * 对面在拿它跟网易外贸通比。
 *
 * 所以规矩是硬的:**页面里不许出现原始 Tailwind 颜色和字号**
 * (`slate-` / `gray-` / `text-sm` / `text-xs` …),要什么从这里拿。
 * eslint 有一条 AST 规则盯着 className 字面量,漏一个 CI 就红。
 *
 * ── 为什么是字符串常量不是组件库 ──────────────────────────────
 *
 * 包一层 `<Card>` `<Button>` 看起来更「正规」,但那要求每个页面先把现有
 * DOM 结构拆掉重搭 —— 这一单是**拉齐**不是重做,6 人天里没有重搭七页的预算。
 * 字符串常量可以逐个 className 换过去,改一行验一行,中途任何时候都是可发布的。
 *
 * 例外只有 `EmptyState` 和 `Segmented` 两个组件(见 ./ui/):
 * 空态必须带 `data-testid="empty-state"`(守卫用例按它数),
 * 靠人记得加 = 迟早漏,做成组件就漏不了。
 *
 * ── 值的出处 ──────────────────────────────────────────────────
 *
 * `apps/landing/src/styles/tokens.css`,一个一个抄的。工厂在官网看到的蓝
 * 和扫码进来看到的蓝必须是同一个。要换色改 tailwind.config.mjs 里
 * `portal.accent` 那三个值,这里不出现任何十六进制。
 */

import { cn } from '../lib/cn.js';

/* ══════════════════════════════════════════════════════════════
   字号 —— 对外产品最小正文 15px(`text`),13px(`meta`)只许给时间戳和标签
   ══════════════════════════════════════════════════════════════ */

export const type = {
  /** 登录页那种一屏一句的大字。正文页面不要用 */
  display: 'text-portal-display font-semibold tracking-tight text-portal-ink',
  /** 页面标题,每页只有一个 */
  h1: 'text-portal-h1 font-semibold tracking-tight text-portal-ink',
  /** 分区标题 */
  h2: 'text-portal-h2 font-semibold text-portal-ink',
  /** 卡片标题 / 列表行主字 */
  h3: 'text-portal-h3 font-semibold text-portal-ink',
  /** 强调正文(卡片里的一句关键话) */
  lead: 'text-portal-lg text-portal-ink',
  /** 正文 */
  body: 'text-portal-body text-portal-ink',
  /** 正文-次要 */
  bodyMuted: 'text-portal-body text-portal-char',
  /** 说明行:标题下面那一句「这一页是干嘛的」 */
  caption: 'text-portal-caption text-portal-char',
  /** 最弱:时间戳、计数、辅助标签。**不许拿来写正文** */
  meta: 'text-portal-meta text-portal-mute',
  /** 表单 label */
  label: 'text-portal-caption font-medium text-portal-ink',
  /** 数字(订单金额、待办计数)。tabular-nums 让列表里的数字对齐 */
  numeral: 'text-portal-h1 font-semibold tabular-nums text-portal-ink',
} as const;

/* ══════════════════════════════════════════════════════════════
   面
   ══════════════════════════════════════════════════════════════ */

export const surface = {
  /** 页面底 */
  page: 'bg-portal-moon',
  /** 卡片:工厂端所有「一块内容」都是这个 */
  card: 'rounded-portal-lg border border-portal-line bg-portal-paper',
  /** 可点的卡片/列表行 —— 手机上 active,桌面上 hover,两个都要 */
  cardInteractive: cn(
    'rounded-portal-lg border border-portal-line bg-portal-paper transition-colors',
    'hover:border-portal-accent/40 hover:bg-portal-tint active:bg-portal-tint2',
  ),
  /** 分区底(卡片里再分一块) */
  inset: 'rounded-portal-md bg-portal-tint',
  /** 发丝线 */
  divider: 'border-portal-line',
} as const;

/* ══════════════════════════════════════════════════════════════
   按钮三档 —— 一屏里 primary 只许有一个
   ══════════════════════════════════════════════════════════════ */

const btnBase = cn(
  'inline-flex items-center justify-center gap-2 rounded-portal-md font-medium',
  'transition-colors disabled:cursor-not-allowed disabled:opacity-45',
  // 键盘可达:工厂端有人用 Tab 走表单,没有焦点环等于用不了
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-portal-accent',
);

export const button = {
  primary: cn(
    btnBase,
    'bg-portal-accent px-4 py-2.5 text-portal-body text-white hover:bg-portal-accent-hover',
  ),
  secondary: cn(
    btnBase,
    'border border-portal-line bg-portal-paper px-4 py-2.5 text-portal-body text-portal-ink',
    'hover:border-portal-accent/50 hover:bg-portal-tint',
  ),
  ghost: cn(btnBase, 'px-3 py-2 text-portal-caption text-portal-char hover:bg-portal-tint2'),
  danger: cn(btnBase, 'bg-portal-danger px-4 py-2.5 text-portal-body text-white hover:opacity-90'),
  /** 手机上通栏的那种主按钮 */
  block: 'w-full',
  /** 小一号,放在列表行右边 */
  compact: 'px-3 py-1.5 text-portal-caption',
} as const;

/* ══════════════════════════════════════════════════════════════
   输入
   ══════════════════════════════════════════════════════════════ */

const fieldBase = cn(
  'w-full rounded-portal-md border border-portal-line bg-portal-paper',
  'text-portal-body text-portal-ink placeholder:text-portal-mute',
  'transition-colors focus:border-portal-accent focus:outline-none focus:ring-2 focus:ring-portal-accent/25',
  'disabled:bg-portal-tint disabled:text-portal-mute',
);

export const field = {
  input: cn(fieldBase, 'px-3 py-2.5'),
  /** 会话页那个回复框自己控制高度,所以不带 rows 相关的东西 */
  textarea: cn(fieldBase, 'resize-none px-3 py-2.5'),
  select: cn(fieldBase, 'px-3 py-2.5'),
  hint: 'mt-1 text-portal-meta text-portal-mute',
  error: 'mt-1 text-portal-caption text-portal-danger',
} as const;

/* ══════════════════════════════════════════════════════════════
   标签 —— 语义四色 + 中性
   ══════════════════════════════════════════════════════════════ */

const badgeBase =
  'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-portal-meta font-medium';

export const badge = {
  neutral: cn(badgeBase, 'bg-portal-tint2 text-portal-char'),
  ok: cn(badgeBase, 'bg-portal-ok-soft text-portal-ok'),
  warn: cn(badgeBase, 'bg-portal-warn-soft text-portal-warn'),
  danger: cn(badgeBase, 'bg-portal-danger-soft text-portal-danger'),
  info: cn(badgeBase, 'bg-portal-info-soft text-portal-info'),
} as const;

/** 语义档 —— 页面算出一个档,直接拿 `badge[tone]` / `banner[tone]`,不要写 if/else 拼 class */
export type Tone = keyof typeof badge;

/* ══════════════════════════════════════════════════════════════
   横幅(整块提示)
   ══════════════════════════════════════════════════════════════ */

const bannerBase =
  'flex items-start gap-2 rounded-portal-md border px-3 py-2.5 text-portal-caption';

export const banner = {
  neutral: cn(bannerBase, 'border-portal-line bg-portal-tint text-portal-char'),
  ok: cn(bannerBase, 'border-portal-ok/25 bg-portal-ok-soft text-portal-ok'),
  warn: cn(bannerBase, 'border-portal-warn/25 bg-portal-warn-soft text-portal-warn'),
  danger: cn(bannerBase, 'border-portal-danger/25 bg-portal-danger-soft text-portal-danger'),
  info: cn(bannerBase, 'border-portal-accent/25 bg-portal-info-soft text-portal-info'),
} as const;

/* ══════════════════════════════════════════════════════════════
   列表
   ══════════════════════════════════════════════════════════════ */

export const list = {
  /** 卡片之间的间隔。工厂端全部用这一个,不要有的 space-y-2 有的 space-y-3 */
  stack: 'space-y-2.5',
  /** 一行的内边距 */
  row: 'px-4 py-3.5',
  /** 未读:整行加粗 —— 只靠一个小圆点在手机上看不见 */
  unread: 'font-semibold text-portal-ink',
} as const;

/* ══════════════════════════════════════════════════════════════
   筛选 chip
   ══════════════════════════════════════════════════════════════ */

export const chip = {
  base: cn(
    'rounded-full px-3.5 py-1.5 text-portal-caption transition-colors',
    'disabled:cursor-not-allowed disabled:opacity-40',
  ),
  on: 'bg-portal-accent text-white',
  off: 'border border-portal-line bg-portal-paper text-portal-char hover:bg-portal-tint',
} as const;

/* ══════════════════════════════════════════════════════════════
   「多久没回」的红黄灰 —— 询盘和客户两页共用一个判据
   ══════════════════════════════════════════════════════════════ */

/**
 * 距上一条入站消息多久了,该显示成什么颜色。
 *
 * 阈值写在这里而不是两个页面各写一遍:询盘列表说「12 小时」是黄的、
 * 客户列表说同一条是灰的,工厂会觉得这系统自己都对不上。
 *
 * 4 小时 / 24 小时这两条线不是随便定的:WhatsApp 的客服窗口是 24 小时,
 * 过了就发不出去 —— 红的含义是「再不回就联系不上了」,不是「有点久」。
 */
export function replyLatencyTone(lastInboundAt: string | null | undefined, now = Date.now()): Tone {
  if (!lastInboundAt) return 'neutral';
  const t = Date.parse(lastInboundAt);
  if (Number.isNaN(t)) return 'neutral';
  const hours = (now - t) / 3_600_000;
  if (hours >= 24) return 'danger';
  if (hours >= 4) return 'warn';
  return 'neutral';
}

/** 上面那个档配的中文。「超过 24 小时」不写「1 天前」—— 24 小时是窗口,天数不是 */
export function replyLatencyLabel(
  lastInboundAt: string | null | undefined,
  now = Date.now(),
): string {
  if (!lastInboundAt) return '';
  const t = Date.parse(lastInboundAt);
  if (Number.isNaN(t)) return '';
  const mins = Math.max(0, Math.floor((now - t) / 60_000));
  if (mins < 60) return `${mins} 分钟未回`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} 小时未回`;
  return `超过 ${Math.floor(hours / 24)} 天未回`;
}

/* ══════════════════════════════════════════════════════════════
   WhatsApp 号的身份色（09-23 一家最多 5 个号）
   ══════════════════════════════════════════════════════════════ */

/**
 * 按号位（slot 1..5）取颜色。**class 字符串必须写全**：Tailwind 只认源码里完整出现的类名，
 * 拼出来的 `bg-portal-acct${n}` 会被摇树摇掉，线上就是一个没颜色的点。
 */
const ACCOUNT_TONES = [
  {
    dot: 'bg-portal-acct1',
    text: 'text-portal-acct1',
    soft: 'bg-portal-acct1-soft',
    ring: 'ring-portal-acct1',
  },
  {
    dot: 'bg-portal-acct2',
    text: 'text-portal-acct2',
    soft: 'bg-portal-acct2-soft',
    ring: 'ring-portal-acct2',
  },
  {
    dot: 'bg-portal-acct3',
    text: 'text-portal-acct3',
    soft: 'bg-portal-acct3-soft',
    ring: 'ring-portal-acct3',
  },
  {
    dot: 'bg-portal-acct4',
    text: 'text-portal-acct4',
    soft: 'bg-portal-acct4-soft',
    ring: 'ring-portal-acct4',
  },
  {
    dot: 'bg-portal-acct5',
    text: 'text-portal-acct5',
    soft: 'bg-portal-acct5-soft',
    ring: 'ring-portal-acct5',
  },
] as const;

export type AccountTone = (typeof ACCOUNT_TONES)[number];

export function accountTone(slot: number | null | undefined): AccountTone {
  const i = Math.max(1, Math.min(5, Math.floor(slot ?? 1))) - 1;
  return ACCOUNT_TONES[i] ?? ACCOUNT_TONES[0];
}
